import { Box, InputAdornment, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCreateOrganizationMutation } from "../../redux/api/OrganizationApi";
import {
  FormPageDescription,
  FormPageSubmitButton,
  FormPageTitle,
  FormPageWrapper,
} from "../../common/styles";
import { validateSpecialCharacters } from "../validateSpecialCharacters";
import { validateEmailDomain } from "../validateEmailDomain";

interface FormValues {
  name: string;
  domain: string;
}

const IdentityComponent = () => {
  const ORGANIZATION_NAME_CHARACTER_LIMIT = 255;
  const [createOrganization] = useCreateOrganizationMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormValues>();

  const onSubmit = async (data: FormValues) => {
    const request = {
      organizationName: data.name.trim(),
      emailDomain: data.domain.trim(),
    };

    await createOrganization(request).unwrap().catch(handleError);
  };

  const handleError = (error: any) => {
    if (error.status === 409) {
      if (error.data.message === "conflict.organization.name.exists") {
        setError("name", {
          type: "custom",
          message: t(`${error.data.message}`),
        });
      } else if (error.data.message === "conflict.organization.domain.exists") {
        setError("domain", {
          type: "custom",
          message: t(`${error.data.message}`),
        });
      }
    } else {
      enqueueSnackbar(t("identity.verification.failed"), {
        variant: "error",
        preventDuplicate: true,
        key: error.status,
      });
    }
  };

  return (
    <FormPageWrapper maxWidth="xs">
      <FormPageTitle variant="h1">{t("identity.title")}</FormPageTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormPageDescription>{t("identity.description")}</FormPageDescription>
        <TextField
          required
          margin="normal"
          autoFocus
          fullWidth
          label={t("identity.organization.label")}
          type="text"
          error={!!errors.name}
          helperText={errors.name?.message}
          {...register("name", {
            validate: validateSpecialCharacters("name"),
            required: { value: true, message: t("validation.name.blank") },
          })}
          inputProps={{
            maxLength: ORGANIZATION_NAME_CHARACTER_LIMIT,
          }}
        />
        <TextField
          required
          margin="normal"
          fullWidth
          label={t("organization.domain")}
          type="text"
          error={!!errors.domain}
          helperText={errors.domain?.message}
          {...register("domain", {
            validate: validateEmailDomain,
            required: { value: true, message: t("validation.domain.blank") },
          })}
          inputProps={{ maxLength: 63 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" disableTypography>
                @
              </InputAdornment>
            ),
          }}
        />
        <FormPageSubmitButton
          color="primary"
          type="submit"
          variant="contained"
          size="medium"
          fullWidth
        >
          {t("identity.finish.button")}
        </FormPageSubmitButton>
      </Box>
    </FormPageWrapper>
  );
};

export default IdentityComponent;
